import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Badge, Button } from "nlib/ui";
import { checkIsBusinessUser } from "selectors/user";
import {
  checkSelectedBusinessHasWhoAsk,
  checkSelectedBusinessRpaMode,
  getCurrentQuickBooksRealmId,
  getGlobalStats,
  getSelectedBusinessData
} from "selectors/businesses";
import { getAccountsData } from "selectors/accounts";
import { getActiveOrganization } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import AuditSections from "nlib/pages/AuditPage/AuditSections";
import AuditSubsectionRoutes from "const/AuditSubsectionRoutes";
import DataStatuses from "const/DataStatuses";
import DateTimeFormats from "const/DateTimeFormats";
import Environment from "utils/Environment";
import IntegrationServices from "const/IntegrationServices";
import Platforms from "utils/Platforms";
import React, { useCallback, useMemo } from "react";
import TaskTypes from "const/TaskTypes";
import Texts from "utils/Texts";
import TransactionsStatuses from "nlib/pages/TransactionsPage/TransactionsStatuses";
import UiRoutes from "const/UiRoutes";
import moment from "moment";

const TASK_TYPES_TO_PATH = {
  [TaskTypes.AUDIT]: `${UiRoutes.AUDIT}/${UiRoutes.TRANSACTIONS}/${AuditSubsectionRoutes.ALL_CATEGORIZED}`,
  [TaskTypes.DOCUMENTS]: UiRoutes.DOCUMENTS,
  [TaskTypes.TRANSACTIONS]: UiRoutes.TRANSACTIONS
};

const TYPE_TO_LANG_ID = {
  [TaskTypes.AUDIT]: "cleanUp",
  [TaskTypes.DOCUMENTS]: "documents",
  [TaskTypes.TRANSACTIONS]: "uncategorized"
};

const TRANSACTION_STATUS_TO_BADGE_STYLE = {
  [DataStatuses.TO_RECONCILE]: "alternative",
  [DataStatuses.NEED_REACTION]: "warning",
  [DataStatuses.TO_REVIEW]: "primary"
};

const AutoTaskContent = ({ type, startDate }) => {
  const history = useHistory();

  const {
    id: selectedBusinessId,
    extraData: { integrationService } = {}
  } = useSelector(getSelectedBusinessData);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const { uiTexts } = useSelector(getTextsData);

  const { businessOrganization } = useSelector(getActiveOrganization);

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const selectedBusinessHasWhoAsk = useSelector(checkSelectedBusinessHasWhoAsk);

  const businessUser = useSelector(checkIsBusinessUser);

  const globalStats = useSelector(getGlobalStats);

  const accountsData = useSelector(getAccountsData);

  const monthKey = moment(startDate).format(DateTimeFormats.API_MONTH_DATE);

  const handleGoToButtonClick = useCallback(() => {
    if (type === TaskTypes.BANK_FEEDS) {
      const { QBO_APP_URL, XERO_APP_URL } = Environment.getProcessEnvVars();

      window.open(quickBooksBusiness ? QBO_APP_URL : XERO_APP_URL, "_blank");

      return;
    }
    if (!TASK_TYPES_TO_PATH[type]) return;

    const fromDate = moment(startDate).startOf("month").format(DateTimeFormats.API_DATE);

    const toDate = moment(startDate).endOf("month").format(DateTimeFormats.API_DATE);

    history.push(`/${selectedBusinessId}${TASK_TYPES_TO_PATH[type]}?fromDate=${fromDate}&toDate=${toDate}`);
  }, [history, quickBooksBusiness, selectedBusinessId, startDate, type]);

  const items = useMemo(() => {
    const stats = globalStats[type] || {};

    switch (type) {
      case TaskTypes.TRANSACTIONS:
        return [DataStatuses.TO_RECONCILE, DataStatuses.NEED_REACTION, DataStatuses.TO_REVIEW].map((status) => {
          const count = stats[status]?.byMonth?.[monthKey]?.count;

          const statusData = TransactionsStatuses.getStatusData([
            status,
            businessOrganization,
            rpaMode && !selectedBusinessHasWhoAsk,
            businessUser
          ]);

          return count && {
            count,
            key: status,
            title: uiTexts[statusData.labelLangId],
            counter: (
              <Badge
                counter inverted
                theme={TRANSACTION_STATUS_TO_BADGE_STYLE[status]}>
                {count}
              </Badge>
            )
          };
        }).filter(Boolean);

      case TaskTypes.DOCUMENTS:
        return [DataStatuses.TO_REVIEW].map((status) => {
          const count = stats[status]?.byMonth?.[monthKey]?.count;

          return count && {
            count,
            key: status,
            title: uiTexts[status],
            counter: <Badge counter inverted theme="negative">{count}</Badge>
          };
        }).filter(Boolean);

      case TaskTypes.AUDIT: {
        const [auditTransactionsSection] = AuditSections;

        return auditTransactionsSection.items.map(({ statProp, nameLangId }) => {
          const count = stats[statProp]?.byMonth?.[monthKey]?.count;

          return count && {
            count,
            key: statProp,
            title: uiTexts[nameLangId],
            counter: <Badge counter inverted theme="negative">{count}</Badge>
          };
        }).filter(Boolean);
      }

      case TaskTypes.BANK_FEEDS:
        return accountsData
          .filter(({ extraData = {} }) => {
            return extraData.xeroBankFeedDisconnected || extraData.quickBooksBankFeedDisconnected;
          })
          .map(({ id, name }) => ({ key: id, title: name }));

      default:
        return [];
    }
  }, [
    accountsData,
    businessOrganization,
    businessUser,
    globalStats,
    monthKey,
    rpaMode,
    selectedBusinessHasWhoAsk,
    type,
    uiTexts
  ]);

  const totalCount = items.reduce((aggregator, item) => aggregator + item.count, 0);

  return (
    <div className={Css.autoTaskContent}>
      <div className={Css.stats}>
        {!!items.length && (
          <div className={Css.header}>
            {type === TaskTypes.BANK_FEEDS ? uiTexts.accounts : moment(startDate).format(DateTimeFormats.MONTH_AND_YEAR_TEXT)}
            {(!!totalCount || !!items.length) && (
              <div className={Css.count}>
                {`${totalCount || items.length} ${({
                  [TaskTypes.TRANSACTIONS]: uiTexts.transactions,
                  [TaskTypes.AUDIT]: uiTexts.issues,
                  [TaskTypes.DOCUMENTS]: uiTexts.documents,
                  [TaskTypes.BANK_FEEDS]: uiTexts.lostConnections.toLowerCase()
                })[type]}`}
              </div>
            )}
          </div>
        )}
        <div className={Css.content}>
          {items.length
            ? items.map(({ key, title, counter }) => {
              return (
                <div key={key}className={Css.item}>
                  <div className={Css.title}>{title}</div>
                  <div>{counter}</div>
                </div>
              );
            }) : <div className={Css.empty}>{uiTexts.allDone}</div>}
        </div>
      </div>
      {(!Platforms.checkIsTouchDevice() || type !== TaskTypes.AUDIT) && (
        <Button
          large block primary
          className={Css.button}
          onClick={handleGoToButtonClick}>
          <span>
            {Texts.replaceVars(uiTexts.goToSection, {
              name: type === TaskTypes.BANK_FEEDS
                ? IntegrationServices.getByValue(integrationService)?.shortLabel
                : uiTexts[TYPE_TO_LANG_ID[type]]
            })}
          </span>
          <Icons.ArrowUpRight />
        </Button>
      )}
    </div>
  );
};

export default React.memo(AutoTaskContent);
